/**
 * eSIM relocation notices (#375) for the eSIM inventory: a group whose eSIMs
 * keep moving between modems, and a profile a failed move left on no modem.
 * Logic (staleness, parsing) lives in utils/esimRelocationNotices.
 */
import { AlertTriangle, ArrowLeftRight } from 'lucide-react';
import type { ESimGroupRelocationHint, ESimProfileNeedsAttention } from '@simcheck/shared-types';
import { Alert, AlertTitle, AlertDescription, Badge } from '@/components/ui';
import { cn } from '@/lib/utils';
import { useDateFormat } from '@/hooks/useDateFormat';
import {
  isRelocationHintToday,
  relocationHintTargetImei,
  noticeDate,
} from '@/utils/esimRelocationNotices';

const WARNING_ALERT = 'border-status-warning bg-status-warning-bg';

/** Small badge for a group row: its eSIMs keep moving between modems. */
export function GroupMovingBadge({ hint, className }: { hint: ESimGroupRelocationHint; className?: string }) {
  return (
    <Badge
      variant="warning"
      className={cn('gap-1 whitespace-nowrap', className)}
      title={`${hint.count} eSIM moves on ${hint.day} (UTC)`}
    >
      <ArrowLeftRight className="w-3 h-3" />
      Moving often
    </Badge>
  );
}

/**
 * Warning on a group whose members were moved between modems too often in a
 * day. `modemModel` / `modemLocation` describe the modem the last move went to,
 * when the page can resolve its IMEI.
 */
export function GroupRelocationAlert({
  hint,
  modemModel,
  modemLocation,
  className,
}: {
  hint: ESimGroupRelocationHint;
  modemModel?: string;
  modemLocation?: string;
  className?: string;
}) {
  const imei = relocationHintTargetImei(hint);
  const when = isRelocationHintToday(hint) ? 'today' : `on ${hint.day}`;
  let lastMove: string | null = null;
  if (imei) {
    const kind = modemModel ? `a ${modemModel} modem` : 'modem';
    const where = modemLocation ? ` at ${modemLocation}` : '';
    lastMove = `The last move went to ${kind}${where} (IMEI ${imei}).`;
  }
  return (
    <Alert className={cn(WARNING_ALERT, className)}>
      <AlertTriangle className="h-4 w-4 text-status-warning" />
      <AlertTitle className="text-status-warning leading-snug">This group's eSIMs keep moving between modems</AlertTitle>
      <AlertDescription className="space-y-1.5 text-foreground">
        <p>
          {hint.count} moves {when} (UTC). Tests that use this group need different kinds of modems, so runs keep
          moving an eSIM off one modem and installing it on another. Each move adds time to the run, and on an
          eSIM with a download limit it uses a download.
        </p>
        <p>
          <span className="font-medium">What to do:</span> install one more eSIM from this group on the kind of
          modem these tests need, so each kind of modem has its own.
          {lastMove ? ` ${lastMove}` : ''}
        </p>
        {!imei && hint.message && <p className="text-muted-foreground">{hint.message}</p>}
      </AlertDescription>
    </Alert>
  );
}

/** Badge for a profile row: a failed move left it on no modem. */
export function ProfileNeedsAttentionBadge({ className }: { className?: string }) {
  return (
    <Badge variant="error" className={cn('gap-1 whitespace-nowrap', className)}>
      <AlertTriangle className="w-3 h-3" />
      Needs attention
    </Badge>
  );
}

/** Alert on a profile's detail view: why it needs attention and what to do. */
export function ProfileNeedsAttentionAlert({
  notice,
  className,
}: {
  notice: ESimProfileNeedsAttention;
  className?: string;
}) {
  const fmt = useDateFormat();
  const at = noticeDate(notice.at);
  const moveFailed = notice.reason === 'relocation_install_failed';
  return (
    <Alert variant="destructive" className={className}>
      <AlertTriangle className="h-4 w-4" />
      <AlertTitle className="leading-snug">{moveFailed ? 'Needs attention: a move to another modem failed' : 'Needs attention'}</AlertTitle>
      <AlertDescription className="space-y-1.5 text-foreground">
        {moveFailed && (
          <p>
            This eSIM was deleted from one modem to move it to another, but the download on the new modem failed.
            It is now on no modem. The failed try may have used one of its limited downloads.
          </p>
        )}
        {notice.message && (
          <p className="text-muted-foreground break-words">
            {notice.message}
            {at ? ` (${fmt.formatDateTime(at)})` : ''}
          </p>
        )}
        <p>
          <span className="font-medium">What to do:</span>{' '}
          {moveFailed
            ? 'check with your eSIM provider how many downloads are left, and update the download limit below if it changed. The next successful download clears this notice.'
            : 'the next successful download clears this notice.'}
        </p>
      </AlertDescription>
    </Alert>
  );
}
